import React, { useState, useEffect } from 'react';

export default function ProConnectLanding() {
  const [spotsRemaining, setSpotsRemaining] = useState(23);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    trade: '',
    licensed: ''
  });

  // Countdown Timer to October 25, 2025
  useEffect(() => {
    const deadline = new Date('2025-10-25T23:59:59')?.getTime();

    const timer = setInterval(() => {
      const now = new Date()?.getTime();
      const distance = deadline - now;

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
        minutes: Math.floor(distance % (1000 * 60 * 60) / (1000 * 60)),
        seconds: Math.floor(distance % (1000 * 60) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (spotsRemaining > 15 && Math.random() > 0.7) {
        setSpotsRemaining((prev) => prev - 1);
      }
    }, 90000);
    return () => clearInterval(interval);
  }, [spotsRemaining]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const required = ['fullName', 'phone', 'email', 'trade', 'licensed'];
    const missing = required?.filter((field) => !formData?.[field]);

    if (missing?.length > 0) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      const response = await fetch('https://formspree.io/f/mwprrjev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: formData?.fullName,
          phone: formData?.phone,
          email: formData?.email,
          trade: formData?.trade,
          licensed: formData?.licensed,
          timestamp: new Date()?.toISOString()
        })
      });

      if (response?.ok) {
        setSpotsRemaining((prev) => prev - 1);
        setShowSuccessModal(true);
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          trade: '',
          licensed: ''
        });
      } else {
        alert('Error submitting application. Please try again.');
      }

    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting application. Please try again.');
    }
  };

  return (
    <div className="bg-white font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/98 backdrop-blur-sm border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/assets/images/proconnect-house-icon-only-1760307561779.png" 
                alt="ProConnect Logo" 
                className="w-14 h-14 object-contain"
              />
              <div>
                <div>
                  <span className="text-xl font-bold text-blue-600">Pro</span>
                  <span className="text-xl font-bold text-gray-900">Connect</span>
                </div>
                <div className="text-xs font-medium text-gray-600">Emergency home services</div>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#benefits" className="font-medium text-gray-600 hover:text-orange-600 transition-colors">Benefits</a>
              <a href="#earnings" className="font-medium text-gray-600 hover:text-orange-600 transition-colors">Earnings</a>
              <a href="/terms-and-conditions" className="font-medium text-gray-600 hover:text-orange-600 transition-colors">Terms</a>
              <a href="#middle-cta" className="font-medium text-gray-600 hover:text-orange-600 transition-colors">Early Access</a>
              <a href="#middle-cta" className="text-white font-semibold px-6 py-3 rounded-lg bg-orange-600 hover:bg-orange-700 transition-all transform hover:scale-105">
                Join Network
              </a>
            </div>
            <button
              className="md:hidden p-2 text-gray-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>

              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-blue-50 to-white pt-20">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #0066CC 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-600/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-600/10 rounded-full blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-32 text-center">
          {/* UPDATED: Countdown Timer Alert */}
          <div className="mb-12">
            <div className="inline-flex flex-col items-center gap-3 px-6 py-4 border-2 border-orange-600 rounded-2xl text-sm font-semibold mb-8 shadow-xl bg-gradient-to-r from-orange-50 to-red-50">
              <div className="flex items-center gap-2 text-orange-700">
                <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" />
                </svg>
                <span className="font-bold text-lg">🔥 FINAL CALL: Applications Close In</span>
              </div>
              
              <div className="flex gap-3">
                {[
                { value: timeLeft?.days, label: 'Days' },
                { value: timeLeft?.hours, label: 'Hours' },
                { value: timeLeft?.minutes, label: 'Min' },
                { value: timeLeft?.seconds, label: 'Sec' }]?.map((item, idx) =>
                <div key={idx} className="flex flex-col items-center bg-white px-3 py-2 rounded-lg shadow-md min-w-[60px]">
                    <span className="text-2xl font-bold text-orange-600">{item?.value}</span>
                    <span className="text-xs text-gray-600 font-medium">{item?.label}</span>
                  </div>
                )}
              </div>
              
              <div className="text-center">
                <span className="font-bold text-orange-700 text-lg">{spotsRemaining} of 30 Spots Remaining</span>
              </div>
            </div>
          </div>

          {/* UPDATED: Stronger headline with location */}
          <div className="mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight text-gray-900">
              Get Consistent Work in Tampa
              <span className="block mt-2 text-orange-600">Keep 80%. Get Paid in 7 Days.</span>
              <span className="block text-3xl md:text-4xl mt-4 text-gray-600">Make $13,000+ More Per Year</span>
            </h1>
            
            {/* UPDATED: Tighter subheadline */}
            <p className="text-lg md:text-xl mb-8 max-w-4xl mx-auto leading-relaxed font-medium text-gray-600">
              Join Tampa Bay's elite network of HVAC, plumbing, and electrical contractors who earn <span className="font-bold text-blue-600">$126,542/year</span> vs. $113,500 solo.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-12 text-lg">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
                <span className="font-semibold text-gray-700">Zero marketing costs</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
                <span className="font-semibold text-gray-700">Paid in 7 days</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
                <span className="font-semibold text-gray-700">Pre-sold customers</span>
              </div>
            </div>
          </div>

          {/* UPDATED: Simplified 5-field form */}
          <div className="max-w-2xl mx-auto mb-20">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl border-2 border-blue-600/20">
              <h3 className="text-xl md:text-2xl font-bold mb-2 text-gray-900">Apply for Founding Contractor Status</h3>
              <p className="mb-6 text-sm md:text-base text-gray-600">Licensed & insured contractors only • HVAC • Plumbing • Electrical • Tampa Bay area</p>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-left text-gray-900">Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData?.fullName}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="w-full px-4 py-4 bg-white border-2 border-gray-300 rounded-lg text-gray-900 text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition-all" />

                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-left text-gray-900">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData?.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      inputMode="tel"
                      className="w-full px-4 py-4 bg-white border-2 border-gray-300 rounded-lg text-gray-900 text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition-all" />

                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-left text-gray-900">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    placeholder="your.email@company.com"
                    inputMode="email"
                    className="w-full px-4 py-4 bg-white border-2 border-gray-300 rounded-lg text-gray-900 text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition-all" />

                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-left text-gray-900">Trade Type *</label>
                    <select
                      name="trade"
                      value={formData?.trade}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 bg-white border-2 border-gray-300 rounded-lg text-gray-900 text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition-all">

                      <option value="">Select your trade</option>
                      <option value="HVAC">HVAC</option>
                      <option value="Plumbing">Plumbing</option>
                      <option value="Electrical">Electrical</option>
                      <option value="Multi-Trade">Multi-Trade</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-left text-gray-900">Licensed & Insured in FL? *</label>
                    <select
                      name="licensed"
                      value={formData?.licensed}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 bg-white border-2 border-gray-300 rounded-lg text-gray-900 text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition-all">

                      <option value="">Select</option>
                      <option value="yes">Yes, licensed & insured</option>
                      <option value="no">No, working on it</option>
                    </select>
                  </div>
                </div>
                
                <button
                  onClick={handleSubmit}
                  className="w-full text-white font-bold text-xl py-5 rounded-xl bg-orange-600 hover:bg-orange-700 transition-all transform hover:scale-105 shadow-2xl hover:shadow-orange-600/50">

                  <span className="flex items-center justify-center">
                    <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" />
                    </svg>
                    Lock In Your Spot - {spotsRemaining} Left
                  </span>
                </button>
                
                {/* UPDATED: Better sub-CTA copy */}
                <div className="space-y-2 text-sm text-gray-600 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                    </svg>
                    <span>Takes 2 minutes • No credit card required</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                    </svg>
                    <span>We'll call you within 24 hours</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="font-semibold text-orange-600">80% commission on first 7 jobs (Founding Contractors only)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto mb-12">
            <div className="bg-white p-6 md:p-8 rounded-xl border border-blue-600/20 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 text-orange-600">$13,042</div>
              <div className="text-base md:text-lg font-semibold mb-1 text-gray-900">Projected More Per Year</div>
              <div className="text-sm text-gray-600">vs. going solo ($126,542 vs $113,500)</div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                <div className="h-2 rounded-full bg-orange-600" style={{ width: '85%' }}></div>
              </div>
              <div className="text-xs mt-2 text-gray-600">Based on projected Tampa Bay averages</div>
            </div>
            
            <div className="bg-white p-6 md:p-8 rounded-xl border border-blue-600/20 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 text-blue-600">$0</div>
              <div className="text-base md:text-lg font-semibold mb-1 text-gray-900">Marketing Costs</div>
              <div className="text-sm text-gray-600">Save $9,600/year on ads & leads</div>
              <div className="px-3 py-1 rounded-full text-xs font-bold mt-4 inline-block bg-blue-600/10 text-blue-600">
                100% HANDLED
              </div>
            </div>
            
            <div className="bg-white p-6 md:p-8 rounded-xl border border-blue-600/20 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 text-blue-600">{spotsRemaining}</div>
              <div className="text-base md:text-lg font-semibold mb-1 text-gray-900">Spots Remaining</div>
              <div className="text-sm text-gray-600">Out of 30 founding contractors</div>
              <div className="flex items-center justify-center mt-4">
                <div className="w-3 h-3 rounded-full animate-pulse bg-orange-600 mr-2"></div>
                <span className="text-xs font-semibold text-orange-600">FILLING FAST</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* NEW: Early Bird Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-50 border-y-4 border-orange-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-6 py-3 bg-yellow-400 text-gray-900 rounded-full font-bold text-lg mb-6 shadow-lg animate-pulse">
              ⭐ FOUNDING CONTRACTOR BENEFITS ({spotsRemaining} Spots Only) ⭐
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Early Birds Get VIP Treatment
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Standard contractors (after launch) get 60% commission.<br />
              <span className="font-bold text-orange-600">Founding Contractors get exclusive perks worth $1,500-2,000</span>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-orange-600 text-center">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">80% Commission<br /><span className="text-orange-600">(Not 60%)</span></h3>
              <p className="text-gray-600 mb-4">Your first 7 jobs through ProConnect, you keep 80%. Normal contractors keep 60%.</p>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 text-left">
                <p className="text-sm font-semibold text-gray-900">Example:</p>
                <p className="text-sm text-gray-700">$1,000 job = <span className="font-bold text-orange-600">you get $800</span> (not $600)</p>
                <p className="text-xs text-gray-600 mt-2">Save $200 × 7 jobs = <span className="font-bold">$1,400 extra</span></p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-blue-600 text-center">
              <div className="text-5xl mb-4">👑</div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Job Priority/<br /><span className="text-blue-600">Seniority</span></h3>
              <p className="text-gray-600 mb-4">When multiple contractors are available, Early Birds get first pick. More opportunities = more money.</p>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-left">
                <p className="text-sm font-semibold text-gray-900">Advantage:</p>
                <p className="text-sm text-gray-700">First access to <span className="font-bold text-blue-600">high-paying emergency calls</span></p>
                <p className="text-xs text-gray-600 mt-2">Est. 3-5 extra jobs/month = <span className="font-bold">$1,200-2,000 more</span></p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-green-600 text-center">
              <div className="text-5xl mb-4">💰</div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Zero Fees<br /><span className="text-green-600">(90 Days)</span></h3>
              <p className="text-gray-600 mb-4">After your first 7 jobs, normal contractors pay monthly platform fees. Early Birds get 90 days FREE.</p>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-left">
                <p className="text-sm font-semibold text-gray-900">Savings:</p>
                <p className="text-sm text-gray-700">$0 fees for 3 months vs. <span className="font-bold text-green-600">standard contractors</span></p>
                <p className="text-xs text-gray-600 mt-2">Total Early Bird Value: <span className="font-bold">$1,500-2,000</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-gray-50" id="benefits">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center px-4 md:px-6 py-3 border border-orange-600/30 rounded-full text-sm font-bold mb-6 md:mb-8 shadow-lg bg-orange-600/10 text-orange-600">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" />
              </svg>
              ⚠️ CONTRACTOR PAIN POINTS
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 md:mb-8 leading-tight text-gray-900">
              Tired of These <span className="block text-orange-600">Contractor Nightmares?</span>
            </h2>
            
            <p className="text-lg md:text-xl max-w-4xl mx-auto leading-relaxed font-medium text-gray-600">
              Stop bleeding money on inefficiencies that drain your profits. 
              <span className="font-bold text-orange-600"> ProConnect eliminates these problems</span> 
              and turns them into competitive advantages.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-16">
            {[
            {
              title: 'Marketing Money Pit',
              cost: '-$9,600/year',
              icon: '💸',
              description: 'Hemorrhaging $800/month on Google Ads, Angi, HomeAdvisor with terrible ROI.',
              solution: '$0 marketing costs - we handle everything',
              points: ['$800/month average marketing spend', '15-20% lead conversion rate', 'Competing with 50+ contractors']
            },
            {
              title: 'Payment Collection Hell',
              cost: '-$4,000/year',
              icon: '💰',
              description: 'Chasing deadbeat customers for months, writing off thousands in bad debt.',
              solution: 'Payment protection - card on file',
              points: ['Average $4,000/year in bad debt', '60+ days average collection time', '10+ hours/month chasing payments']
            },
            {
              title: 'Wasted Drive Time',
              cost: '-$3,600/year',
              icon: '🚗',
              description: '45-minute drives between jobs, 30% of day behind windshield.',
              solution: '10-15 min tight routing',
              points: ['45+ minutes between jobs', '30% time spent driving', 'Only 7.5 jobs/week possible']
            }]?.map((problem, idx) =>
            <div key={idx} className="group relative">
                <div className="absolute inset-0 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-70 group-hover:opacity-100 bg-gradient-to-br from-orange-600/10 to-orange-600/20"></div>
                <div className="relative bg-white border border-orange-600/20 rounded-2xl p-6 md:p-8 transition-all duration-500 transform group-hover:-translate-y-4 group-hover:shadow-2xl">
                  <div className="relative mb-6 md:mb-8">
                    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center border border-orange-600/30 bg-orange-600/10 text-4xl">
                      {problem?.icon}
                    </div>
                    <div className="absolute -top-2 -right-2 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-bounce bg-orange-600">
                      {problem?.cost}
                    </div>
                  </div>

                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">{problem?.title}</h3>
                  <p className="mb-4 md:mb-6 leading-relaxed text-sm md:text-base text-gray-600">{problem?.description}</p>

                  <div className="space-y-3 mb-4 md:mb-6">
                    {problem?.points?.map((point, i) =>
                  <div key={i} className="flex items-center space-x-3">
                        <div className="w-2 h-2 rounded-full bg-orange-600"></div>
                        <span className="text-xs md:text-sm text-gray-600">{point}</span>
                      </div>
                  )}
                  </div>

                  <div className="border-t border-blue-600/20 pt-4 md:pt-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-bold text-base md:text-lg text-blue-600">ProConnect Solution</div>
                        <div className="text-xs md:text-sm text-blue-600">{problem?.solution}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Final CTA Section - Orange Box - UPDATED with 5 fields */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-orange-600 to-orange-700" id="middle-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-white/10 backdrop-blur-sm p-8 md:p-12 rounded-2xl shadow-2xl border border-white/20">
            <div className="mb-6 md:mb-8">
              {/* UPDATED: More urgent headline */}
              <div className="inline-flex flex-col items-center gap-2 px-4 py-3 text-white rounded-xl text-sm font-medium mb-6 bg-gray-900/80 shadow-lg">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" />
                  </svg>
                  <span className="font-bold text-lg">Applications Close: {timeLeft?.days}d {timeLeft?.hours}h {timeLeft?.minutes}m</span>
                </div>
                <span className="font-bold text-xl text-orange-400">Only {spotsRemaining} Founding Spots Left</span>
              </div>
              
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
                Final Call: Lock In Founding Contractor Benefits NOW
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-6 md:mb-8">
                After October 25th: Standard contractors only (60% commission, no priority)<br className="hidden md:block" />
                <strong className="text-yellow-300">Don't miss 80% commission on your first 7 jobs</strong>
              </p>
            </div>

            {/* UPDATED: 5-field form */}
            <div className="max-w-2xl mx-auto mb-6 md:mb-8">
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-left text-white">Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData?.fullName}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="w-full px-4 py-4 bg-white/90 border-2 border-white/50 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-white/30 transition-all" />

                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-left text-white">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData?.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      inputMode="tel"
                      className="w-full px-4 py-4 bg-white/90 border-2 border-white/50 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-white/30 transition-all" />

                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-left text-white">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    placeholder="your.email@company.com"
                    inputMode="email"
                    className="w-full px-4 py-4 bg-white/90 border-2 border-white/50 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-white/30 transition-all" />

                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-left text-white">Trade Type *</label>
                    <select
                      name="trade"
                      value={formData?.trade}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 bg-white/90 border-2 border-white/50 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-white/30 transition-all">

                      <option value="">Select your trade</option>
                      <option value="HVAC">HVAC</option>
                      <option value="Plumbing">Plumbing</option>
                      <option value="Electrical">Electrical</option>
                      <option value="Multi-Trade">Multi-Trade</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-left text-white">Licensed & Insured? *</label>
                    <select
                      name="licensed"
                      value={formData?.licensed}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 bg-white/90 border-2 border-white/50 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-white/30 transition-all">

                      <option value="">Select</option>
                      <option value="yes">Yes, licensed & insured</option>
                      <option value="no">No, working on it</option>
                    </select>
                  </div>
                </div>
                
                <button
                  onClick={handleSubmit}
                  className="w-full text-white font-bold text-xl py-6 rounded-xl bg-gray-900 hover:bg-gray-800 transition-all transform hover:scale-105 shadow-2xl">

                  Claim My Founding Contractor Spot Now →
                </button>
              </div>
            </div>

            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-white/80 mb-4 md:mb-6">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" />
                </svg>
                <span className="text-sm">License Verified</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
                <span className="text-sm">No Long-Term Contract</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
                <span className="text-sm">Tampa Bay Local</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Earnings Section */}
      <section className="py-16 md:py-20 bg-white" id="earnings">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 text-gray-900">
              The ProConnect Advantage: Projected Numbers
            </h2>
            <p className="text-lg md:text-xl max-w-3xl mx-auto text-gray-600">
              See projected potential for how ProConnect contractors could out-earn solo operators year after year
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center mb-12 md:mb-16">
            {/* Earnings Comparison Chart */}
            <div className="bg-white p-6 md:p-8 rounded-2xl border border-blue-600/20 shadow-lg">
              <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-gray-900">Annual Earnings Comparison</h3>
              
              <div className="mb-4 md:mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm md:text-base text-gray-600">Solo Contractor (Projected)</span>
                  <span className="text-xl md:text-2xl font-bold text-gray-600">$113,500</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div className="h-4 rounded-full bg-gray-600" style={{ width: '75%' }}></div>
                </div>
                <div className="text-xs md:text-sm mt-1 text-gray-600">After marketing costs, bad debt, fuel, admin time (projected)</div>
              </div>

              <div className="mb-4 md:mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm md:text-base text-orange-600">ProConnect Contractor (Projected)</span>
                  <span className="text-xl md:text-2xl font-bold text-orange-600">$126,542</span>
                </div>
                <div className="w-full rounded-full h-4 bg-orange-600/10">
                  <div className="h-4 rounded-full bg-orange-600" style={{ width: '100%' }}></div>
                </div>
                <div className="text-xs md:text-sm mt-1 text-orange-600">Projected: Zero marketing costs, payment protection, tight routing</div>
              </div>

              <div className="border-t border-blue-600/20 pt-4 mt-4 md:mt-6">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-sm md:text-base text-blue-600">Your Projected Advantage</span>
                  <span className="text-2xl md:text-3xl font-bold text-blue-600">+$13,042</span>
                </div>
                <div className="text-xs md:text-sm mt-1 text-blue-600">Projected extra per year with ProConnect</div>
              </div>
              
              <div className="mt-4 md:mt-6 p-4 border border-orange-600/20 rounded-lg bg-orange-600/5">
                <p className="text-xs text-gray-600">
                  <strong>Important:</strong> All figures are projections based on industry averages and are not guaranteed. 
                  Actual results may vary significantly based on individual performance and market conditions.
                </p>
              </div>
            </div>

            {/* Key Benefits List */}
            <div className="space-y-4 md:space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-orange-600/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2 text-gray-900">Zero Marketing Costs</h4>
                  <p className="text-sm md:text-base text-gray-600">Potential to save $9,600/year on Google Ads, Facebook, Angi, HomeAdvisor. We handle 100% of marketing and deliver pre-sold customers to you.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-blue-600/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2 text-gray-900">Payment Protection</h4>
                  <p className="text-sm md:text-base text-gray-600">Card on file before you arrive. Payment protection system designed to minimize bad debt. Potential to eliminate $4,000+/year in uncollected payments.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-blue-600/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2 text-gray-900">More Jobs, Less Driving</h4>
                  <p className="text-sm md:text-base text-gray-600">Tight 10-15 mile radius means potential 10-15 min between jobs vs. 45 min. Projected 10 jobs/week vs. 7.5 jobs, potential savings of $3,600/year in fuel.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-blue-600/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2 text-gray-900">Get Your Life Back</h4>
                  <p className="text-sm md:text-base text-gray-600">We handle scheduling, billing, payments, customer service. Potential to save 8+ hours/week on admin work. Focus on what you do best - HVAC, plumbing, or electrical service.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* NEW: FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Common Questions Answered</h2>
            <p className="text-lg text-gray-600">Everything Tampa contractors want to know about ProConnect</p>
          </div>

          <div className="space-y-6">
            {[
            {
              q: "What's the catch? Why is the commission so high?",
              a: "No catch. We make money from homeowner memberships ($45-70/month), not from cutting your pay. Higher commission = better contractors = better service = more members. Everyone wins."
            },
            {
              q: "Do I have to accept every job?",
              a: "Nope. You see the job details and choose whether to accept. You control your schedule completely."
            },
            {
              q: "What if I'm already busy with my own clients?",
              a: "Perfect! Use ProConnect to fill gaps during slow periods. This is supplemental income, not replacement. Many contractors use us for 30-40% of their work."
            },
            {
              q: "How do payments work exactly?",
              a: "Customer pays us upfront. You complete the job. Within 7 days, money hits your account via direct deposit. Simple."
            },
            {
              q: "What areas of Tampa do you serve?",
              a: "All of Hillsborough County (86 ZIP codes). You set your service radius—we only send jobs in your area."
            },
            {
              q: "What happens after the first 30 contractors?",
              a: "We'll expand slowly. Later contractors get standard 60% (not 80%), no priority status, and may have waitlist. Lock in Early Bird now."
            }]?.map((faq, idx) =>
            <div key={idx} className="bg-white p-6 rounded-xl border border-blue-600/20 shadow-md hover:shadow-lg transition-all">
                <h4 className="text-lg font-bold mb-3 text-gray-900 flex items-start">
                  <span className="text-orange-600 mr-2">Q:</span>
                  {faq?.q}
                </h4>
                <p className="text-gray-600 pl-6 leading-relaxed">
                  <span className="font-semibold text-blue-600">A:</span> {faq?.a}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 text-gray-900">
              Why Tampa Bay's Best Contractors Choose ProConnect
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Real feedback from contractors who've experienced our pre-launch pilot program
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
            {
              name: 'Mike Rodriguez',
              role: 'HVAC Contractor, Wesley Chapel',
              quote: 'I was skeptical about platform fees, but when I saw I\'d save $9,600 on marketing alone, it was a no-brainer. First month: $8,200 more revenue than my best solo month.',
              rating: 5,
              avatar: 'MR'
            },
            {
              name: 'Sarah Chen',
              role: 'Plumbing Contractor, Tampa',
              quote: 'The payment protection feature alone pays for the platform fee. No more chasing down deadbeat customers. Plus, the jobs are all within 15 minutes - I get home for dinner now.',
              rating: 5,
              avatar: 'SC'
            },
            {
              name: 'James Thompson',
              role: 'Electrical Contractor, Riverview',
              quote: 'I was working 70-hour weeks trying to manage everything myself. ProConnect handles the admin so I can focus on what I\'m best at. Work-life balance restored.',
              rating: 5,
              avatar: 'JT'
            }]?.map((testimonial, idx) =>
            <div key={idx} className="bg-gray-50 p-6 md:p-8 rounded-xl border border-blue-600/20 shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mr-4 text-white font-bold">
                    {testimonial?.avatar}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial?.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial?.role}</p>
                    <div className="flex items-center mt-1">
                      <span className="text-xs font-medium px-2 py-1 rounded bg-orange-600/10 text-orange-600">Licensed & Insured</span>
                    </div>
                  </div>
                </div>
                <blockquote className="italic mb-4 text-sm md:text-base text-gray-600">
                  "{testimonial?.quote}"
                </blockquote>
                <div className="flex items-center">
                  <div className="flex text-orange-600">
                    {[...Array(testimonial?.rating)]?.map((_, i) =>
                  <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                  )}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">5.0 • Pilot Program</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Critical Disclaimer Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white p-6 md:p-8 rounded-lg border-l-4 border-orange-600 shadow-lg">
            <h4 className="font-bold mb-4 md:mb-6 flex items-center text-lg md:text-xl text-gray-900">
              <svg className="w-6 h-6 mr-3 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" />
              </svg>
              CRITICAL DISCLAIMER - PROJECTED EARNINGS ONLY
            </h4>
            <div className="text-sm md:text-base space-y-3 md:space-y-4 text-gray-600">
              <p><strong className="text-gray-900">All numbers above are projections and estimates only.</strong> ProConnect does not guarantee any specific income levels, cost savings, or financial outcomes for HVAC, plumbing, or electrical contractors.</p>
              <p><strong className="text-gray-900">Payment Protection Limitations:</strong> "Payment protection" and related payment features are subject to successful customer payment processing. If customers cannot or do not pay, contractor payment may not be guaranteed.</p>
              <p><strong className="text-gray-900">Individual Results Vary:</strong> Your actual earnings, savings, and business outcomes may be significantly different from these projections based on factors including but not limited to: market conditions, work quality, customer satisfaction, economic factors, competition, trade specialization (HVAC vs. plumbing vs. electrical), and individual business practices.</p>
              <p><strong className="text-gray-900">No Financial Advice:</strong> These projections should not be considered financial advice. Consult with financial professionals before making business decisions.</p>
              <p><strong className="text-gray-900">Multi-Trade Considerations:</strong> While projections are based on HVAC industry averages, actual results for plumbing and electrical contractors may vary due to different market dynamics, pricing structures, and demand patterns in the Tampa Bay area.</p>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="/assets/images/proconnect-house-icon-only-1760307561779.png" 
                  alt="ProConnect Logo" 
                  className="w-14 h-14 object-contain"
                />
                <div>
                  <span className="text-xl font-bold text-blue-400">Pro</span>
                  <span className="text-xl font-bold text-white">Connect</span>
                </div>
              </div>
              <p className="mb-6 max-w-md text-gray-400">
                Tampa Bay's premier home service marketplace connecting HVAC, plumbing, and electrical contractors with premium customers.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#benefits" className="hover:text-white transition-colors">Benefits</a></li>
                <li><a href="#earnings" className="hover:text-white transition-colors">Earnings</a></li>
                <li><a href="#middle-cta" className="hover:text-white transition-colors">Early Access</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Pros@proconnect247.com</li>
                <li>(813) 537-6058</li>
                <li>Tampa Bay, FL</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-sm text-gray-400">© 2025 ProConnect. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
      {/* Success Modal */}
      {showSuccessModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform animate-in">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to the ProConnect Founding Team!
              </h3>
              
              <p className="text-gray-600 mb-6">
                We've received your application. Our team will review your license and contact you within 24 hours.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>What's Next?</strong><br />
                  ✓ We'll verify your license<br />
                  ✓ Review your application<br />
                  ✓ Call you to discuss next steps<br />
                  ✓ Lock in your 80% Early Bird commission
                </p>
              </div>
              
              <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all">

                Got It, Thanks!
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  );

}